import {useEffect} from "react"
import {useAppDispatch, useAppSelector} from "@/store/hooks"
import {selectAuth} from "@/store/auth/authSelectors"
import {loadNotificationSettingsSaga, updateNotificationSettingsSaga} from "@/store/auth/authSlice"
import type {NotificationSettings} from "@/store/auth/authSlice"
import AccountSettingsTitle from "@/components/modals/AccountSettingsModal/components/AccountSettingsTitle/AccountSettingsTitle"
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn"
import FormTitle from "@/components/ui/Form/components/FormTitle/FormTitle"
import FormDivider from "@/components/ui/Form/components/FormDivider/FormDivider"
import {showToast} from "@/components/app/Toast/showToast"
import Toggle from "@/components/ui/Toggle/Toggle"
import {TextMidd, TextNorm} from "@/components/ui/Text"
import css from "./AccountSettingsModalNotifications.module.css"

const AccountSettingsModalNotifications = () => {
    const dispatch = useAppDispatch()
    const auth = useAppSelector(selectAuth)
    const settings = auth?.notificationSettings

    useEffect(() => {
        dispatch(loadNotificationSettingsSaga())
    }, [dispatch])

    const update = (patch: Partial<NotificationSettings>) => {
        dispatch(updateNotificationSettingsSaga(patch))
        showToast('Notification settings saved')
    }

    if (!settings) return null

    return (
        <>
            <AccountSettingsTitle>Notifications</AccountSettingsTitle>
            <FormDivider />
            <FormColumn gap={16}>
                <FormTitle>Email notifications</FormTitle>
                <div className={css.row}>
                    <div className={css.rowText}>
                        <TextNorm>Email notifications</TextNorm>
                        <TextMidd className={css.description}>Get emails for unread notifications</TextMidd>
                    </div>
                    <Toggle checked={settings.emailEnabled} onChange={(v) => update({emailEnabled: v})} />
                </div>
            </FormColumn>
            {settings.emailEnabled && (
                <>
                    <FormDivider />
                    <FormColumn gap={16}>
                        <FormTitle>Notify me about</FormTitle>
                        <div className={css.group}>
                            <div className={css.row}>
                                <div className={css.rowText}>
                                    <TextNorm>Assignments</TextNorm>
                                    <TextMidd className={css.description}>When a task is assigned to you</TextMidd>
                                </div>
                                <Toggle checked={settings.assignments} onChange={(v) => update({assignments: v})} />
                            </div>
                            <div className={css.divider} />
                            <div className={css.row}>
                                <div className={css.rowText}>
                                    <TextNorm>Comments & replies</TextNorm>
                                    <TextMidd className={css.description}>New comments on tasks you're participating in</TextMidd>
                                </div>
                                <Toggle checked={settings.comments} onChange={(v) => update({comments: v})} />
                            </div>
                            <div className={css.divider} />
                            <div className={css.row}>
                                <div className={css.rowText}>
                                    <TextNorm>Mentions</TextNorm>
                                    <TextMidd className={css.description}>When someone @mentions you in a comment</TextMidd>
                                </div>
                                <Toggle checked={settings.mentions} onChange={(v) => update({mentions: v})} />
                            </div>
                            <div className={css.divider} />
                            <div className={css.row}>
                                <div className={css.rowText}>
                                    <TextNorm>Weekly digest</TextNorm>
                                    <TextMidd className={css.description}>A Monday email with your open tasks</TextMidd>
                                </div>
                                <Toggle checked={settings.weeklyDigest} onChange={(v) => update({weeklyDigest: v})} />
                            </div>
                        </div>
                    </FormColumn>
                </>
            )}
            <FormDivider />
            <FormColumn gap={16}>
                <FormTitle>Product updates</FormTitle>
                <div className={css.row}>
                    <div className={css.rowText}>
                        <TextNorm>Announces</TextNorm>
                        <TextMidd className={css.description}>News about new features and announcements</TextMidd>
                    </div>
                    <Toggle checked={settings.productUpdates} onChange={(v) => update({productUpdates: v})} />
                </div>
            </FormColumn>
        </>
    )
}

export default AccountSettingsModalNotifications
